/**
 * Tradução do jargão náutico para quem não é navegador — funções puras
 * (testadas em `format.test.ts`).
 *
 * "9,4 nós, rumo 087°, calado 7,2 m" é exato e não diz nada ao leitor do
 * AmaView. O portal existe para o cidadão acompanhar a Amazônia, e o cidadão
 * pensa em km/h e em "para o leste".
 *
 * O número técnico não é escondido: ele continua ali, entre parênteses, para
 * quem sabe ler. O que muda é qual dos dois vem primeiro.
 */

/** Um nó é uma milha náutica por hora = 1,852 km/h, por definição. */
export const KMH_POR_NO = 1.852;

/** "9,4 nós (17 km/h)". */
export function velocidade(sog: number | null): string | null {
  if (sog === null || !Number.isFinite(sog)) return null;
  const kmh = sog * KMH_POR_NO;
  // Sempre com a casa decimal, e por isso sempre no plural: em português
  // "1,0 nós" é a forma corrente quando o número vem com decimal.
  const nos = sog.toFixed(1).replace('.', ',');
  return `${nos} nós (${Math.round(kmh)} km/h)`;
}

const ROSA = [
  'norte',
  'nor-nordeste',
  'nordeste',
  'és-nordeste',
  'leste',
  'és-sudeste',
  'sudeste',
  'su-sudeste',
  'sul',
  'su-sudoeste',
  'sudoeste',
  'oés-sudoeste',
  'oeste',
  'oés-noroeste',
  'noroeste',
  'nor-noroeste',
] as const;

/** Ponto cardeal do rumo: 87° → "leste". */
export function cardeal(graus: number | null): string | null {
  if (graus === null || !Number.isFinite(graus)) return null;
  const g = ((graus % 360) + 360) % 360;
  return ROSA[Math.round(g / 22.5) % 16]!;
}

/** "rumo leste (087°)". */
export function rumo(graus: number | null): string | null {
  const c = cardeal(graus);
  if (c === null) return null;
  const g = ((graus! % 360) + 360) % 360;
  return `rumo ${c} (${String(Math.round(g)).padStart(3, '0')}°)`;
}

/** "136 × 20 m" — só quando os dois lados existem. */
export function dimensoes(comprimento: number | null, boca: number | null): string | null {
  if (comprimento === null || !Number.isFinite(comprimento)) return null;
  const c = Math.round(comprimento);
  if (boca === null || !Number.isFinite(boca)) return `${c} m de comprimento`;
  return `${c} × ${Math.round(boca)} m`;
}

/**
 * Calado com a glosa curta: sem ela, "7,2 m" parece a altura do navio.
 * É a parte submersa, e é o que decide se ele passa no rio na seca.
 */
export function calado(m: number | null): string | null {
  if (m === null || !Number.isFinite(m) || m <= 0) return null;
  return `${m.toFixed(1).replace('.', ',')} m abaixo da linha d'água`;
}

/**
 * Idade de uma recepção, em palavras. A hora sozinha engana quando o dado
 * pode ter horas: "01:38" parece recente às 02:00 e é de ontem às 23:00.
 */
export function idade(ms: number, agora: number = Date.now()): string {
  const min = Math.max(0, (agora - ms) / 60_000);
  if (min < 2) return 'agora há pouco';
  if (min < 60) return `há ${Math.round(min)} min`;
  const h = min / 60;
  if (h < 24) return `há ${Math.round(h)} h`;
  const d = Math.round(h / 24);
  return d === 1 ? 'há 1 dia' : `há ${d} dias`;
}

/** Acima disso a idade da posição vira aviso no topo da ficha, não rodapé. */
export const IDADE_AVISO_MS = 60 * 60 * 1000;
